<template>
	<div class="vcx-mt-50">
		<p class="vcx-body-sm vcx-font-style-italic vcx-text-gray-40"
			>Hypothetical performance of investment between {{ startDateOfHypotheticalInvestment }} and today with
			dividend reinvestment enabled. <strong>Past performance does not guarantee future results.</strong></p
		>
		<div class="vcx-flex vcx-flex-col-smo vcx-mt-100-smo vcx-mt-200-md">
			<div
				v-for="(item, index) in overviewItems"
				:key="index"
				:class="[
					'vcx-flex vcx-flex-col-md gap-50-md vcx-items-center-smo vcx-justify-between-smo vcx-py-25-smo',
					{ 'vcx-bl-md vcx-ml-200-md vcx-pl-200-md': index !== 0 }
				]"
			>
				<p class="vcx-text-gray-30"
					>{{ item.label }}<sup v-if="item.footnote">{{ item.footnote }}</sup></p
				>

				<div v-if="appStore.isMobile" class="vcx-body-md-bold vcx-py-50">{{ item.value }}</div>
				<h3 v-else class="vcx-subheading-bold">{{ item.value }}</h3>
			</div>
		</div>

		<div class="vcx-mt-100-smo vcx-mt-200-md vcx-p-100 vcx-p-150-md vcx-border">
			<VcxTabs v-if="showTabs" :default-tab-index="defaultTabIndex" :tabs="tabs" />
			<div v-else>
				<VcxSimulatedReturnsValueChart
					v-if="props.showValueChart && props.fund.simulatedInvestmentReturns"
					:chart-data="props.fund.simulatedInvestmentReturns"
					:initial-investment="initialInvestmentValue"
				/>
				<VcxSimulatedReturnsNetReturnsChart
					v-if="props.showNetReturnChart && props.fund.simulatedReturnsByTimePeriod"
					:is-annualized="isAnnualized"
					:returns-by-time-period="props.fund.simulatedReturnsByTimePeriod"
					:total-investment-value="currentInvestmentValue"
					:show-distribution-footnote="isInnovationFund"
					:fund-id="props.fund.fundriseId"
				/>
			</div>
		</div>
		<div class="vcx-body-sm vcx-text-tertiary vcx-mt-300 vcx-mt-500-md">
			<p>Past performance is not indicative of future results.</p>
			<p
				>The performance assumptions shown in each of these charts are for illustrative purposes only, should
				not be relied on for any other purpose, and are not intended to reflect the actual experience of any
				individual investor. These values indicate returns based on net asset value and may not accurately
				reflect the amount that might be paid for your shares in a market transaction. Closed-end funds
				frequently trade at a discount to their net asset value.</p
			>
			<p
				><strong>Value chart:</strong> This chart shows the hypothetical growth of an investment in this
				offering over the period indicated, assuming dividend reinvestment.</p
			>
			<p
				><strong>Net return chart:</strong> This chart shows the hypothetical returns on an investment in this
				offering over the period indicated, assuming dividend reinvestment.</p
			>
		</div>
	</div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import { formattedPerformanceCurrencyValue } from '@utils/performance-portfolio';
import { Fund } from 'types/funds';
import { growthTechReitId } from '@constants/reit';
import spacetime from 'spacetime';
import { Tab } from 'types/layout';
import { useAppStore } from '@stores/app';
import VcxSimulatedReturnsNetReturnsChart from '@components/vcx/vcx-simulated-net-returns-chart.vue';
import VcxSimulatedReturnsValueChart from '@components/vcx/vcx-simulated-returns-value-chart.vue';
import VcxTabs from '@components/vcx/vcx-tabs-component.vue';

interface Props {
	fund: Fund;
	showValueChart: boolean;
	showNetReturnChart: boolean;
}

const props = defineProps<Props>();

const appStore = useAppStore();

const isAnnualized = computed(
	(): boolean => props.fund.simulatedReturnsByTimePeriod?.allTimeReturnDetails?.annualized ?? false
);

const defaultTabIndex = computed((): number => {
	return tabs.value.length > 1 && props.fund.objective?.name === 'Cash flow' ? 1 : 0;
});

const currentInvestmentValue = computed(
	(): string | undefined => props.fund.simulatedInvestmentReturnsHeaders?.currentInvestmentValue
);

const initialInvestmentValue = computed(
	(): string | undefined => props.fund.simulatedInvestmentReturnsHeaders?.initialInvestmentValue
);

const netAppreciation = computed(
	(): string | undefined => props.fund.simulatedReturnsByTimePeriod?.allTimeReturnDetails?.netAppreciation
);

const netDividends = computed(
	(): string | undefined => props.fund.simulatedReturnsByTimePeriod?.allTimeReturnDetails?.netDividends
);

const startDateOfHypotheticalInvestment = computed((): string =>
	spacetime(props.fund.hypotheticalFirstInvestmentDate).format(CustomSpacetimeFormat.MONTH_SHORT_YEAR)
);

const isInnovationFund = computed((): boolean => props.fund.fundriseId === growthTechReitId);

const overviewItems = computed(
	(): Array<{
		label: string;
		value: string;
		footnote?: string;
	}> => {
		return [
			{
				label: 'Initial investment value',
				value: formattedPerformanceCurrencyValue(initialInvestmentValue.value ?? '0')
			},
			{
				label: 'Total appreciation',
				value: formattedPerformanceCurrencyValue(netAppreciation.value ?? '0')
			},
			{
				label: 'Total distributions',
				value: formattedPerformanceCurrencyValue(netDividends.value ?? '0'),
				footnote: isInnovationFund.value ? '1' : undefined
			},
			{
				label: 'Current investment value',
				value: formattedPerformanceCurrencyValue(currentInvestmentValue.value ?? '0')
			}
		];
	}
);

const tabs = computed((): Array<Tab> => {
	const tabsToReturn: Array<Tab> = [];

	if (props.showValueChart) {
		tabsToReturn.push({
			key: 'value',
			label: 'Value',
			panel: {
				component: VcxSimulatedReturnsValueChart,
				props: {
					chartData: props.fund.simulatedInvestmentReturns,
					initialInvestment: initialInvestmentValue.value
				}
			}
		});
	}

	if (props.showNetReturnChart) {
		tabsToReturn.push({
			key: 'net-returns',
			label: 'Net return',
			panel: {
				component: VcxSimulatedReturnsNetReturnsChart,
				props: {
					isAnnualized: isAnnualized.value,
					returnsByTimePeriod: props.fund.simulatedReturnsByTimePeriod,
					totalInvestmentValue: currentInvestmentValue.value,
					showDistributionFootnote: isInnovationFund.value
				}
			}
		});
	}

	return tabsToReturn;
});

const showTabs = computed((): boolean => tabs.value.length > 1);
</script>
